import dayjs, { Dayjs } from 'dayjs'

/*
 * The backend keeps every date in UTC and the model is told to write dates it shows as
 *   {{date:<ISO>}}  or  {{datetime:<ISO>}}  or  {{datetime:<ISO>|<IANA time zone>}}
 * (the zone variant is for appointments and calendar slots, which are shown in their own zone,
 * as on the case screen). These are rendered here before markdown parsing, always in an
 * explicit zone: the token's own, else the organisation's (defaultTimeZone). The browser's
 * zone is only the last resort when the caller has no organisation zone yet.
 * A value that is a day alone (2026-03-04: a due date, a filter the model echoes) is a calendar
 * date, not an instant: it is shown as written, in no zone.
 * Inside a markdown table the model escapes the zone's separator ("\|"): it is read the same.
 */
const DATE_TOKEN = /\{\{(date|datetime):([^}|\s\\]+)(?:\\?\|([^}\s]+))?\}\}/g
const TRAILING_PARTIAL_TOKEN = /\{\{[^}]*\}?$/
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

const DEFAULT_DATE_FORMAT = 'DD MMM YYYY'

export interface AssistantDateOptions {
  dateFormat?: string
  streaming?: boolean
  /** The organisation's time zone (appStore.timezone) for tokens that carry no zone. */
  defaultTimeZone?: string
  /**
   * The label the app's lists put after a time shown in its own zone - "(AEDT, Sydney)", from
   * appStore.getTimeZoneDisplayName. Given, a token with a zone always carries it, as an
   * appointment does on the IME list; without it (or when it has none) the zone is named only
   * when it is not the organisation's.
   */
  zoneLabel?: (_instant: string, _timeZone: string) => string | null | undefined
}

const zoneAbbreviation = (instant: Date, timeZone: string): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-AU', { timeZone, timeZoneName: 'short' })
        .formatToParts(instant)
        .find(p => p.type === 'timeZoneName')?.value ?? timeZone
    )
  } catch {
    return timeZone
  }
}

/** False when the zone is missing or not an IANA zone the runtime knows. */
const isKnownZone = (utc: Dayjs, timeZone: string | undefined): boolean => {
  if (!timeZone) {
    return false
  }
  try {
    return utc.tz(timeZone).isValid()
  } catch {
    return false
  }
}

const formatToken = (
  kind: string,
  value: string,
  tokenTimeZone: string | undefined,
  dateFormat: string,
  defaultTimeZone: string | undefined,
  zoneLabel?: AssistantDateOptions['zoneLabel']
): string => {
  // Values without an offset are UTC by contract.
  const utc = dayjs.utc(value)
  if (!utc.isValid()) {
    return value
  }

  if (CALENDAR_DATE.test(value)) {
    return utc.format(dateFormat)
  }

  const fallbackTimeZone =
    defaultTimeZone && isKnownZone(utc, defaultTimeZone)
      ? defaultTimeZone
      : dayjs.tz.guess()
  const timeZone =
    tokenTimeZone && isKnownZone(utc, tokenTimeZone)
      ? tokenTimeZone
      : fallbackTimeZone
  const zoned = utc.tz(timeZone)

  if (kind === 'date') {
    return zoned.format(dateFormat)
  }

  const text = zoned.format(`${dateFormat} HH:mm`)

  // A time in its own zone is labelled the way the app's lists label an appointment.
  const label =
    tokenTimeZone && timeZone === tokenTimeZone
      ? zoneLabel?.(utc.toISOString(), timeZone)
      : null
  if (label) {
    return `${text} ${label}`
  }

  // Otherwise the zone is only named when it is not the one the rest of the app is showing.
  return timeZone !== fallbackTimeZone
    ? `${text} ${zoneAbbreviation(utc.toDate(), timeZone)}`
    : text
}

/**
 * Replaces date tokens in assistant text with formatted dates in the token's zone or, without
 * one, in defaultTimeZone. While streaming, a token that has not fully arrived yet is hidden
 * instead of flashing as raw text.
 */
export const renderAssistantDates = (
  content: string,
  options: AssistantDateOptions = {}
): string => {
  if (!content) {
    return content
  }

  const dateFormat = options.dateFormat || DEFAULT_DATE_FORMAT
  const rendered = content.replace(
    DATE_TOKEN,
    (_match, kind: string, value: string, timeZone?: string) =>
      formatToken(
        kind,
        value,
        timeZone,
        dateFormat,
        options.defaultTimeZone,
        options.zoneLabel
      )
  )

  return options.streaming
    ? rendered.replace(TRAILING_PARTIAL_TOKEN, '')
    : rendered
}
